/**
 * Surviving a deploy that happens while somebody has the app open.
 *
 * THE FAILURE THIS EXISTS FOR, observed in production.
 *
 * Every build names its chunks by content hash, and a tab that loaded `index.html`
 * before a deploy goes on asking for the hashes that index.html named. Navigate to a
 * route whose chunk was only ever in the previous build and the request is for a file
 * the origin no longer has.
 *
 * What comes back is not a 404. The distribution rewrites 403 and 404 to `index.html`
 * with a 200 so that React Router owns deep links - which is correct for a route and
 * exactly wrong for an asset. The browser asked for a module and was handed HTML, so
 * it reports "Expected a JavaScript-or-Wasm module script but the server responded
 * with a MIME type of text/html", the dynamic import rejects, and React unmounts the
 * tree into a blank page. Nothing on screen suggests the cause is an old tab.
 *
 * Scoping the rewrite to everything-except-/assets would be the tidier fix and
 * CloudFront does not offer it: error responses are set per distribution, not per
 * behaviour. So the recovery belongs here.
 *
 * ONE RELOAD, GUARDED. A fresh `index.html` names the chunks that do exist, so a
 * single reload fixes it. The guard is what makes that safe: if the reload does not
 * fix it - a genuinely broken deploy, an offline tab - the second failure rethrows
 * and the error surfaces, rather than the page reloading for ever in front of
 * somebody who can only watch.
 */

import { lazy, type ComponentType } from 'react';

/** Set while a recovery reload is in flight, so it can only happen once. */
const RELOADED = 'planning-chunk-reloaded';

/**
 * `React.lazy`, with one reload if the chunk has gone from the origin.
 *
 * The success path clears the guard, so a tab left open across two separate deploys
 * can recover from each of them. Keying it to the session rather than a module
 * variable is deliberate: the reload destroys module state, so a module variable
 * would always read as "first attempt" and never stop.
 */
export function lazyChunk<T extends ComponentType<unknown>>(
  load: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const mod = await load();
      try {
        sessionStorage.removeItem(RELOADED);
      } catch {
        // Private mode. The guard simply does not persist, which fails safe: the
        // worst case is one extra reload rather than none.
      }
      return mod;
    } catch (error) {
      let alreadyTried = true;
      try {
        alreadyTried = sessionStorage.getItem(RELOADED) !== null;
        if (!alreadyTried) {
          sessionStorage.setItem(RELOADED, '1');
        }
      } catch {
        // Cannot read or write the guard, so cannot prove this is the first attempt.
        // Rethrow rather than risk a reload loop: a visible error beats a page that
        // will not stop reloading.
        throw error;
      }

      if (alreadyTried) {
        throw error;
      }

      window.location.reload();
      // Never settles. The reload takes the page before React could render anything
      // from this promise, and resolving it would flash a component that is about to
      // be thrown away.
      return new Promise<{ default: T }>(() => {});
    }
  });
}
